import React, { useEffect, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { reinitScrollAnimations } from './utils/scrollAnimation';

// Critical components - load immediately (above the fold)
import Home from './component/Home';
import FloatingCTA from './component/FloatingCTA';

// Lazy load all routes for code splitting and better performance
const Tour = lazy(() => import('./component/Tour'));
const Review = lazy(() => import('./component/Review'));
const About = lazy(() => import('./component/About'));
const Travel = lazy(() => import('./component/Travel'));
const ServicePage = lazy(() => import('./component/ServicePage'));
const CarRental = lazy(() => import('./component/CarRental'));
const RoutePage = lazy(() => import('./component/RoutePage'));
const RouteNotFound = lazy(() => import('./component/RouteNotFound'));
const BlogList = lazy(() => import('./component/blog/BlogList'));
const BlogArticle = lazy(() => import('./component/blog/BlogArticle'));
const PrivacyPolicy = lazy(() => import('./component/PrivacyPolicy'));
const NotFound = lazy(() => import('./component/404'));

// New SEO Priority Pages - Lazy loaded
const ServiceHub = lazy(() => import('./component/services/ServiceHub'));
const AirportTransfer = lazy(() => import('./component/services/AirportTransfer'));
const IntercityTaxi = lazy(() => import('./component/services/IntercityTaxi'));
const ServicePageNew = lazy(() => import('./component/services/ServicePage'));
const Booking = lazy(() => import('./component/Booking'));
const AirportPage = lazy(() => import('./component/airport/AirportPage'));
const LocationPage = lazy(() => import('./component/location/LocationPage'));
const IntercityRoutePage = lazy(() => import('./component/intercity/IntercityRoutePage'));
const CharterPage = lazy(() => import('./component/charter/CharterPage'));
const Contact = lazy(() => import('./component/Contact'));
const Pricing = lazy(() => import('./component/Pricing'));
const Fleet = lazy(() => import('./component/Fleet'));
const FAQ = lazy(() => import('./component/FAQ'));
const Terms = lazy(() => import('./component/Terms'));
const Sitemap = lazy(() => import('./component/Sitemap'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '50vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    กำลังโหลด...
  </div>
);

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    // Reinitialize scroll animations when route changes
    reinitScrollAnimations();
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route exact path="/" element={<Home />} />
          <Route path="/tour" element={<Tour />} />
          <Route path="/about" element={<About />} />
          <Route path="/reviews" element={<Review />} />
          
          {/* Service Pages */}
          <Route path="/taxi-khon-kaen" element={<ServicePage />} />
          <Route path="/car-rental-khon-kaen" element={<ServicePage />} />
          <Route path="/airport-transfer-khon-kaen" element={<ServicePage />} />
          <Route path="/intercity-taxi-khon-kaen" element={<ServicePage />} />
          
          {/* Car Rental Landing Page - Thai and English URLs */}
          <Route path="/เหมารถ" element={<CarRental />} />
          <Route path="/car-rental" element={<CarRental />} />
          
          {/* Route Pages - Khon Kaen to Udon Thani / Cham Chanoat */}
          <Route path="/เหมารถ-ขอนแก่น-อุดรธานี" element={<RoutePage />} />
          <Route path="/รถเหมา-ขอนแก่น-คำชะโนด" element={<RoutePage />} />
          <Route path="/รถรับส่ง-ขอนแก่น-อุดร" element={<RoutePage />} />
          
          {/* Route Pages - Khon Kaen to Maha Sarakham */}
          <Route path="/เหมารถ-ขอนแก่น-มหาสารคาม" element={<RoutePage />} />
          <Route path="/รถเหมา-ขอนแก่น-มมส" element={<RoutePage />} />
          <Route path="/รถรับส่ง-สนามบิน-มหาสารคาม" element={<RoutePage />} />
          
          {/* Route Pages - Khon Kaen to Chiang Khan / Loei */}
          <Route path="/เหมารถ-ขอนแก่น-เชียงคาน" element={<RoutePage />} />
          <Route path="/รถเหมา-ขอนแก่น-เลย" element={<RoutePage />} />
          <Route path="/รถรับส่ง-ท่องเที่ยว-เชียงคาน" element={<RoutePage />} />
          
          {/* Route Pages - Airport Transfer */}
          <Route path="/รถรับส่ง-สนามบิน-ขอนแก่น" element={<RoutePage />} />
          <Route path="/เหมารถ-สนามบิน-ขอนแก่น-เมือง" element={<RoutePage />} />
          <Route path="/รถเหมา-airport-khonkaen" element={<RoutePage />} />
          
          {/* Route 404 Handler - Must be before catch-all */}
          <Route path="/เหมารถ-*" element={<RouteNotFound />} />
          
          {/* Blog Pages */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          
          {/* Privacy Policy */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          {/* Travel Pages */}
          <Route path="/bluelagoon" element={<Travel />} />
          <Route path="/takayai" element={<Travel />} />
          <Route path="/phamanfun" element={<Travel />} />
          <Route path="/haina" element={<Travel />} />
          <Route path="/changarena" element={<Travel />} />
          <Route path="/watphasonkaew" element={<Travel />} />
          <Route path="/ubonrat" element={<Travel />} />
          <Route path="/khaokho" element={<Travel />} />
          <Route path="/phuthapbuek" element={<Travel />} />
          <Route path="/airport" element={<Travel />} />
          
          {/* Booking */}
          <Route path="/booking" element={<Booking />} />
          <Route path="/booking/" element={<Booking />} />
          
          {/* Support Pages - Phase 2F & 3E */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact/" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing/" element={<Pricing />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/fleet/" element={<Fleet />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/faq/" element={<FAQ />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/terms/" element={<Terms />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/sitemap/" element={<Sitemap />} />
          
          {/* New SEO Priority Pages - Service Hub & Main Services */}
          <Route path="/services" element={<ServiceHub />} />
          <Route path="/services/" element={<ServiceHub />} />
          <Route path="/services/airport-transfer" element={<AirportTransfer />} />
          <Route path="/services/airport-transfer/" element={<AirportTransfer />} />
          <Route path="/services/intercity-taxi" element={<IntercityTaxi />} />
          <Route path="/services/intercity-taxi/" element={<IntercityTaxi />} />
          
          {/* Service Pages - Phase 2A (must be after specific routes) */}
          <Route path="/services/:serviceSlug" element={<ServicePageNew />} />
          
          {/* Airport Pages - Phase 2B (must be after /airport static route) */}
          <Route path="/airport/:airportName" element={<AirportPage />} />
          
          {/* Intercity Route Pages - Phase 2D */}
          <Route path="/intercity/:route" element={<IntercityRoutePage />} />
          
          {/* Charter Pages - Phase 2E */}
          <Route path="/charter/:charterSlug" element={<CharterPage />} />
          
          {/* Location Pages - Phase 2C (must be last before catch-all) */}
          <Route path="/:province/taxi-service" element={<LocationPage />} />
          
          {/* 404 Not Found - ต้องอยู่ท้ายสุด */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <FloatingCTA />
      {/* Cookie Consent - ซ่อนไว้ชั่วคราว ยังไม่ได้ใช้งานจริง */}
      {/* TODO: อนาคตถ้าต้องการใช้ให้ uncomment บรรทัดด้านล่าง */}
      {/* <CookieConsent /> */}
    </>
  );
}

function App() {
  return (
    <>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </>
  );
}

export default App;
